import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Star from '../components/Star';
import { STATUS } from '../../constants/result';
import { isAnyUrlMatchesPathname } from '../utils/browserUtils';
import { PAGES_WITH_CAPTIONS } from '../../constants/url';
import AccessibilitySettingsDropdown from '../components/AccessibilitySettingsDropdown/AccessibilitySettingsDropdown';

function Header({
    playlistForRender,
    studentName,
    studentGrade,
    showStars,
    shouldShowLogoutButton,
    showModal,
    showSettingsPopup,
    location,
}) {
    const [formattedStudentGrade, setFormattedStudentGrade] = useState('');
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);
    const [remainingSpace, setRemainingSpace] = useState(0);
    const [logoutButtonVisible, setLogoutButtonVisible] = useState(false);
    const [headerContainerStyles, setHeaderContainerStyles] = useState({});

    const starSectionRef = useRef();
    const studentNameRef = useRef();
    const studentGradeRef = useRef();
    const starsContainerRef = useRef();
    const animationFrameRef = useRef(null);

    // Centers the student name/grade container within its parent and sets positioning styles
    const calculateHeaderPosition = useCallback(() => {
        const parentContainer = document.getElementById('container');
        const studentNameGradeContainer = document.getElementById('lantern-student');

        if (!parentContainer || !studentNameGradeContainer) {
            setHeaderContainerStyles({});
            return;
        }

        const parentContainerWidth = parentContainer.offsetWidth || 0;
        const studentNameGradeContainerWidth = studentNameGradeContainer.offsetWidth || 0;

        if (parentContainerWidth === 0) {
            setHeaderContainerStyles({});
            return;
        }

        // Calculate total width and left margin for positioning
        const leftPosition = Math.max(
            0,
            parentContainerWidth / 2 - studentNameGradeContainerWidth / 2,
        );
        const width = Math.max(0, parentContainerWidth / 2 + studentNameGradeContainerWidth / 2);

        setHeaderContainerStyles({
            left: `${leftPosition}px`,
            width: `${width}px`,
        });
    }, []);

    const scheduleHeaderPositionUpdate = useCallback(() => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        animationFrameRef.current = requestAnimationFrame(() => {
            calculateHeaderPosition();
            animationFrameRef.current = null;
        });
    }, [calculateHeaderPosition]);

    const formatStudentGrade = useCallback(() => {
        if (!studentGrade) {
            setFormattedStudentGrade('');
            return;
        }

        const grade = Number(studentGrade);
        const formatted = Number.isNaN(grade) ? studentGrade : `G${studentGrade}`;
        setFormattedStudentGrade(formatted);
    }, [studentGrade]);

    const calculateLayout = useCallback(() => {
        if (!starsContainerRef.current) {
            setIsContentOverflowing(false);
            setRemainingSpace(-1);
            return;
        }

        try {
            const fullContainer = starsContainerRef.current.offsetWidth || 0;

            if (fullContainer === 0) {
                setIsContentOverflowing(false);
                setRemainingSpace(-1); // Use -1 to indicate "no width constraint"
                return;
            }

            const starsWidth = starSectionRef.current ? starSectionRef.current.offsetWidth || 0 : 0;
            const studentNameWidth = studentNameRef.current
                ? studentNameRef.current.offsetWidth || 0
                : 0;
            const studentGradeWidth = studentGradeRef.current
                ? studentGradeRef.current.offsetWidth || 0
                : 0;
            const extraSpaces = 60;
            const totalContentSize =
                starsWidth + studentNameWidth + studentGradeWidth + extraSpaces;
            const calculatedRemainingSpace = Math.max(0, fullContainer - totalContentSize);

            setIsContentOverflowing(totalContentSize > fullContainer);
            setRemainingSpace(calculatedRemainingSpace);
            // eslint-disable-next-line no-unused-vars
        } catch (error) {
            setIsContentOverflowing(false);
            setRemainingSpace(-1);
        }
    }, []);

    const hideLogoutButton = useCallback(() => {
        setLogoutButtonVisible(false);
    }, []);

    const handleOutsideClick = useCallback(
        e => {
            if (logoutButtonVisible && e.target && !e.target.matches('.dropmenu-trigger')) {
                hideLogoutButton();
            }
        },
        [logoutButtonVisible, hideLogoutButton],
    );

    const toggleLogoutButton = useCallback(() => {
        setLogoutButtonVisible(prev => shouldShowLogoutButton && !prev);
    }, [shouldShowLogoutButton]);

    const handleDropMenuClick = useCallback(
        e => {
            e.stopPropagation();
            toggleLogoutButton();
        },
        [toggleLogoutButton],
    );

    const handleLogoutAction = useCallback(() => {
        toggleLogoutButton();
        showModal();
    }, [toggleLogoutButton, showModal]);

    const renderCaret = useCallback(() => {
        return logoutButtonVisible ? ' \u25B2' : ' \u25BC';
    }, [logoutButtonVisible]);

    const renderStars = useCallback(() => {
        return playlistForRender.map((playlistItem, index) => (
            <Star
                key={`star-${playlistItem.key}`}
                index={index + 1}
                isFilled={playlistItem.status === STATUS.COMPLETED}
                withShadow={true}
            />
        ));
    }, [playlistForRender]);

    const renderStudentInfo = useCallback(() => {
        // Only apply width styles if we have valid remaining space AND content is overflowing
        const studentDetailStyles =
            isContentOverflowing && remainingSpace > 0
                ? {
                      width: `${remainingSpace}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                  }
                : {};

        return (
            <div className="student-detail" style={studentDetailStyles}>
                <div className="student-name" ref={studentNameRef} onClick={handleDropMenuClick}>
                    {studentName}
                </div>
                <div className="dash">-</div>
                <div className="student-grade" ref={studentGradeRef}>
                    {formattedStudentGrade}
                </div>
            </div>
        );
    }, [
        isContentOverflowing,
        remainingSpace,
        formattedStudentGrade,
        studentName,
        handleDropMenuClick,
    ]);

    useEffect(() => {
        formatStudentGrade();
    }, [formatStudentGrade]);

    useEffect(() => {
        calculateLayout();
    }, [playlistForRender, studentGrade, calculateLayout]);

    useEffect(() => {
        if (!shouldShowLogoutButton && logoutButtonVisible) {
            hideLogoutButton();
        }
    }, [shouldShowLogoutButton, logoutButtonVisible, hideLogoutButton]);

    useEffect(() => {
        calculateLayout();
        calculateHeaderPosition();

        const eventCleanupFunctions = [];

        if (shouldShowLogoutButton) {
            window.addEventListener('click', handleOutsideClick, { passive: true });
            window.addEventListener('touchend', handleOutsideClick, { passive: true });

            eventCleanupFunctions.push(() => {
                window.removeEventListener('click', handleOutsideClick, { passive: true });
                window.removeEventListener('touchend', handleOutsideClick, { passive: true });
            });
        }

        const handleResize = scheduleHeaderPositionUpdate;

        if (window.screen && 'orientation' in window.screen && window.screen.orientation) {
            window.screen.orientation.addEventListener('change', handleResize);
            eventCleanupFunctions.push(() => {
                if (window.screen && window.screen.orientation) {
                    window.screen.orientation.removeEventListener('change', handleResize);
                }
            });
        } else if ('orientationchange' in window) {
            window.addEventListener('orientationchange', handleResize, { passive: true });
            eventCleanupFunctions.push(() => {
                window.removeEventListener('orientationchange', handleResize, { passive: true });
            });
        }

        window.addEventListener('resize', handleResize, { passive: true });
        eventCleanupFunctions.push(() => {
            window.removeEventListener('resize', handleResize, { passive: true });
        });

        const cleanupAnimationFrame = () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };

        return () => {
            eventCleanupFunctions.forEach(cleanup => {
                cleanup();
            });

            cleanupAnimationFrame();
        };
    }, [
        shouldShowLogoutButton,
        handleOutsideClick,
        scheduleHeaderPositionUpdate,
        calculateLayout,
        calculateHeaderPosition,
    ]);

    return (
        <nav className="assessment-header navbar navbar-fixed-top">
            <div id="container" className="container">
                <div className="lantern-brand">
                    <span>mCLASS&#174;</span>
                </div>
                <div
                    id="header-container-inner"
                    className="header-container-inner align-right"
                    style={headerContainerStyles}
                >
                    <div className="starts-container" ref={starsContainerRef}>
                        {showStars && (
                            <div
                                className={`header-stars gray-star stars-count-${playlistForRender.length} stars-on-0`}
                                ref={starSectionRef}
                                role="group"
                                aria-label="Progress indicators"
                            >
                                {renderStars()}
                            </div>
                        )}
                        {renderStudentInfo()}
                        <div id="lantern-student" className="lantern-student">
                            <div
                                onClick={handleDropMenuClick}
                                className="dropmenu-trigger"
                                role="button"
                                tabIndex={shouldShowLogoutButton ? 0 : -1}
                                aria-label="Toggle logout menu"
                                aria-expanded={logoutButtonVisible}
                            >
                                {shouldShowLogoutButton && renderCaret()}
                            </div>
                            {logoutButtonVisible && (
                                <div
                                    className="dropmenu show"
                                    onClick={handleLogoutAction}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Log out"
                                >
                                    Log Out
                                </div>
                            )}
                        </div>
                        {isAnyUrlMatchesPathname(location.pathname, PAGES_WITH_CAPTIONS) && (
                            <AccessibilitySettingsDropdown showSettingsPopup={showSettingsPopup} />
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}

Header.propTypes = {
    playlistForRender: PropTypes.array,
    studentName: PropTypes.string,
    studentGrade: PropTypes.string,
    showStars: PropTypes.bool,
    shouldShowLogoutButton: PropTypes.bool,
    showModal: PropTypes.func,
    showSettingsPopup: PropTypes.func,
    location: PropTypes.object,
};

Header.defaultProps = {
    playlistForRender: [],
    studentName: '',
    studentGrade: '',
    showStars: true,
    shouldShowLogoutButton: false,
    showModal: () => {},
    showSettingsPopup: () => {},
    location: {},
};

export default Header;
